/**
 * Pure boot-gate decision logic.
 *
 * Extracted so it is unit-testable and shared by AuthContext (initial state)
 * and App.js (route guard). No React / router / network dependencies.
 *
 * Why this exists: the app must NOT block first paint on the
 * `/partners/domain/{host}` branding fetch. A single slow/cold response (e.g.
 * right after a deploy) previously froze the whole UI behind a full-screen
 * spinner for up to the request's 10s timeout. Branding color resolves
 * synchronously elsewhere; the ONLY first-paint decision that needs the (async)
 * partner flags is the guest redirect on the two entry routes ("/", "/main"),
 * so that is the only thing we ever briefly defer — and only when we cannot
 * resolve the guest-upload flag synchronously.
 */

/** Routes whose guest redirect depends on the (async) guest-upload flag. */
export const GUEST_ENTRY_ROUTES = ["/", "/main"];

/**
 * Initial guest-upload flag for first paint. A cached cookie from a prior visit
 * wins; otherwise fall back to the static tenant default so partner guests are
 * routed correctly without waiting on the network. The backend fetch reconciles
 * this value afterwards on every boot.
 *
 * @param {{ cookieValue?: string, tenantAllowGuestUpload?: boolean }} args
 * @returns {boolean}
 */
export function resolveInitialAllowGuestUpload({
  cookieValue,
  tenantAllowGuestUpload = false,
} = {}) {
  if (cookieValue === "true") return true;
  if (cookieValue === "false") return false;
  return Boolean(tenantAllowGuestUpload);
}

/**
 * Whether to gate the guest entry routes at boot. True ONLY for a genuine first
 * visit to an unrecognized domain (no known tenant AND no cached partner
 * cookies). Known tenants (e.g. serhant) and returning users never gate, so
 * they render immediately regardless of backend latency.
 *
 * @param {{ tenantId?: string|null, hasBrandingCookie?: boolean, hasGuestUploadCookie?: boolean }} args
 * @returns {boolean}
 */
export function shouldGateBoot({
  tenantId,
  hasBrandingCookie = false,
  hasGuestUploadCookie = false,
} = {}) {
  const knownTenant = Boolean(tenantId);
  const hasCachedFlags = Boolean(hasBrandingCookie) && Boolean(hasGuestUploadCookie);
  return !(knownTenant || hasCachedFlags);
}

/**
 * Whether the RequireAuth guard should briefly defer (show a loader) instead of
 * making the guest redirect. Only for unauthenticated users on the two entry
 * routes while the flag is still loading. Every other route/user renders
 * immediately — branding never blocks the app.
 *
 * @param {{ loaderDomain?: boolean, isAuthenticated?: boolean, pathname?: string }} args
 * @returns {boolean}
 */
export function shouldDeferGuestEntryRoute({
  loaderDomain = false,
  isAuthenticated = false,
  pathname = "",
} = {}) {
  return (
    Boolean(loaderDomain) &&
    !isAuthenticated &&
    GUEST_ENTRY_ROUTES.includes(pathname)
  );
}
